import React, { useEffect } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import {
  MANAGER_COMMENT_LABELS,
  MANAGER_COMMENT_MAX_LENGTH,
} from '~/components/CommentCell/constants.ts';
import { commentCellStyles } from '~/components/CommentCell/styles.ts';
import { formatCommentValue, getCommentPlainText } from '~/components/CommentCell/utils.ts';

interface IManagerCommentDialogProps {
  initialComment?: string;
  open: boolean;
  readOnly: boolean;
  isSubmitting: boolean;
  onSave?: (newComment: string) => Promise<void> | void;
  onClose: () => void;
  setIsSubmitting: (val: boolean) => void;
  labels?: Partial<Record<keyof typeof MANAGER_COMMENT_LABELS, string>>;
}

interface IManagerCommentFormValues {
  comment: string;
}

export const ManagerCommentDialog: React.FC<IManagerCommentDialogProps> = ({
  initialComment,
  open,
  readOnly,
  isSubmitting,
  onSave,
  onClose,
  setIsSubmitting,
  labels,
}) => {
  const resolvedLabels = { ...MANAGER_COMMENT_LABELS, ...labels };
  const formMethods = useForm<IManagerCommentFormValues>({
    defaultValues: {
      comment: '',
    },
  });
  const { handleSubmit, reset } = formMethods;
  const initialCommentText = getCommentPlainText(initialComment);

  useEffect(() => {
    if (open) {
      reset({ comment: getCommentPlainText(initialComment) });
    }
  }, [initialComment, open, reset]);

  const handleSave: SubmitHandler<IManagerCommentFormValues> = async ({ comment }) => {
    const nextComment = formatCommentValue(comment);

    if (!onSave) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(nextComment);
      onClose();
    } catch (error) {
      console.error(resolvedLabels.saveError, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (readOnly) {
    return (
      <Dialog open={open} onClose={onClose} sx={commentCellStyles.managerReadOnlyDialog}>
        <DialogTitle sx={commentCellStyles.managerReadOnlyTitle}>
          {resolvedLabels.readOnlyTitle}
        </DialogTitle>
        <DialogContent sx={commentCellStyles.clientDialogContent}>
          <Typography sx={commentCellStyles.managerReadOnlyCommentText}>
            {initialCommentText}
          </Typography>
        </DialogContent>
        <DialogActions sx={commentCellStyles.clientDialogActions}>
          <Button onClick={onClose} variant="contained" sx={commentCellStyles.clientCloseButton}>
            {resolvedLabels.close}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} sx={commentCellStyles.managerDialog}>
      <FormProvider {...formMethods}>
        <Box sx={commentCellStyles.managerDialogHeader}>
          <DialogTitle sx={commentCellStyles.managerDialogTitle}>
            {resolvedLabels.dialogTitle}
          </DialogTitle>
          <IconButton
            onClick={onClose}
            sx={commentCellStyles.managerDialogCloseIcon}
            aria-label={resolvedLabels.closeAria}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={commentCellStyles.managerDialogContent}>
          <Box sx={commentCellStyles.managerEditor}>
            <FormInput
              name="comment"
              disabled={isSubmitting}
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              variant="standard"
              placeholder={resolvedLabels.placeholder}
              slotProps={{
                input: { disableUnderline: true },
                htmlInput: { maxLength: MANAGER_COMMENT_MAX_LENGTH },
              }}
              sx={commentCellStyles.managerEditorInput}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={commentCellStyles.managerDialogActions}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={commentCellStyles.managerCancelButton}
            disabled={isSubmitting}
          >
            {resolvedLabels.cancel}
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit(handleSave)}
            sx={commentCellStyles.managerSaveButton}
            startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
          >
            {isSubmitting ? resolvedLabels.saving : resolvedLabels.save}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
